import React from 'react';
import { DiabetesPageTemplate } from './DiabetesPageTemplate';
import { diabetesImages } from './diabetesPageData';

const GestationalDiabetes = () => {
  return (
    <DiabetesPageTemplate
      eyebrow="Pregnancy & Diabetes"
      title="Gestational Diabetes"
      intro="Gestational Diabetes Mellitus is a common glucose disorder during pregnancy. It may improve after delivery, but it still needs close care because it can affect both mother and child."
      heroSummary="The right combination of testing, food guidance, activity, and treatment can support a safer pregnancy."
      heroImage={diabetesImages.img3}
      heroAlt="Pregnancy care and gestational diabetes monitoring"
      sections={[
        {
          title: 'What Is Gestational Diabetes?',
          paragraphs: [
            'Prevalence in India: 3.8% to 21% depending on region and diagnostic methods. Higher in urban areas than rural areas.',
            'Gestational Diabetes Mellitus (GDM) is a condition that occurs during pregnancy when blood glucose levels are higher than normal. It is a common disorder among women of reproductive age and can have serious consequences for both mother and child. GDM is associated with an increased risk of developing type 2 diabetes later in life.',
          ],
          image: diabetesImages.photoImg,
          imageAlt: 'Pregnancy health consultation',
          imageCaption: 'Early diagnosis during pregnancy helps protect both maternal and fetal health.',
        },
        {
          title: 'Risk Factors For Gestational Diabetes',
          items: [
            'Family history of diabetes',
            'Race and ethnicity (higher risk in non-Hispanic Black women)',
            'Advanced maternal age (over 35 years)',
            'Previous history of GDM',
            'Polycystic ovary syndrome (PCOS)',
            'High birth weight baby',
            'Multiple gestations (twins, triplets, etc.)',
            'Hypertension',
            'Preeclampsia',
            'History of macrosomia (baby > 9 lbs 4 oz)',
            'Smoking during pregnancy',
            'Alcohol consumption during pregnancy',
            'Obesity or overweight during pregnancy',
            'Lack of physical activity',
            'Stressful life events',
          ],
        },
        {
          title: 'Prevention And Management',
          paragraphs: [
            'Prevention strategies: Maintain a healthy weight before pregnancy, eat a balanced diet, exercise regularly, and avoid tobacco use.',
            'Management: Monitor blood glucose levels throughout pregnancy and adjust insulin doses as needed to keep them within target ranges.',
          ],
        },
        {
          title: 'Treatment Options',
          columns: [
            {
              title: 'Lifestyle Modifications',
              items: [
                'Weight management',
                'Balanced diet',
                'Regular physical activity',
              ],
            },
            {
              title: 'Medicines',
              items: [
                'Oral medications such as metformin can help lower blood glucose levels.',
                'Insulin therapy is necessary for some women to maintain target glucose levels.',
              ],
            },
          ],
          image: diabetesImages.img4,
          imageAlt: 'Nutrition and treatment support during pregnancy',
          imageCaption: 'Some women manage with lifestyle measures alone, while others also need medicines or insulin.',
        },
        {
          title: 'Nutritional Counseling',
          paragraphs: [
            'Working with a registered dietitian can help develop a personalized meal plan to manage blood glucose levels and reduce complications.',
          ],
          columns: [
            {
              title: "Foods To Emphasize",
              items: [
                'Low-carb, high-fiber: Meat, fish, poultry, eggs, vegetables, fruits, whole grains, legumes.',
                'Nutrient-dense options: Fresh produce, lean proteins, healthy fats in moderation.',
                'General rule: Focus on whole, unprocessed foods that provide essential nutrients without excess calories.',
              ],
            },
            {
              title: 'Foods To Limit Or Avoid',
              items: [
                'Sugary items: Sugary drinks, sweets, candies, cakes, pastries, desserts.',
                'High-fat and saturated fat: Butter, cream, cheese, full-fat dairy, fatty meats, fried foods.',
                'High-sodium foods: Processed foods, canned soups, fast food, salty snacks.',
                'Refined grains: White bread, white rice, pasta.',
                'Trans fats: Margarine, shortening, partially hydrogenated oils.',
                'Artificial sweeteners: Diet sodas, energy drinks, some flavored yogurts.',
                'Food additives and preservatives: Packaged snacks, frozen meals, deli meats, hot dogs, lunch meats.',
                'Common allergens if sensitive: Wheat, soy, milk, egg, shellfish.',
                'Caffeine: Coffee, tea, cola (limit intake).',
                'MSG: Bouillon cubes, instant noodles, seasoning mixes, Chinese or Asian cuisine.',
                'Aspartame: Diet sodas and sugar-free products.',
              ],
            },
          ],
          note: 'Always consult with your healthcare provider or dietitian to tailor dietary choices to your individual needs.',
        },
      ]}
    />
  );
};

export default GestationalDiabetes;
